<template>
  <div class="form-wrapper registration-form">
    <form
      novalidate
      @keyup.enter="onSubmit"
      @submit.prevent="onSubmit"
    >
      <BaseInputEmail
        v-if="isActiveTabEmail"
        key="email"
        v-model="email"
        :data-gtm-id="GTM_REG.ID.ga_registration_click_input_email"
        class="registration-form__input"
        focus
        :error-messages="errorMessages.email"
        @input="errorMessages.email = []"
      />
      <BaseInputPhone
        v-else
        key="phone"
        v-model="phone"
        :data-gtm-id="GTM_REG.ID.ga_registration_click_input_phone"
        class="registration-form__input"
        focus
        :error-messages="errorMessages.phone"
        @input="errorMessages.phone = []"
        @onValidate="v => isInternalValidPhone = v"
      />
      <BaseInputPassword
        ref="PasswordInput"
        v-model="password"
        :data-gtm-id="GTM_REG.ID.ga_registration_click_input_password"
        :error-messages="errorMessages.password"
        class="registration-form__input"
        password-strength
        :placeholder="$t('regAuthForgot.placeholder.password')"
        autocomplete="new-password"
        @input="errorMessages.password = []"
      />
      <UiBtn
        :data-gtm-id="GTM_REG.ID.ga_registration_click_btn_registration"
        submit
        color="primary"
        full-width
        :disabled="formHasDisplayedErrors || isSubmitDisabled"
        :loading="isSubmitDisabled"
      >
        {{ $t('popup.register.title') }}
        <UiBadge
          background="solid"
          size="small"
          icon-right="common/gift-filled"
          type="secondary"
          text="+ Бонус"
          class="registration-form__bonus-badge"
        />
      </UiBtn>
      <RegAuthToggle v-if="showRegAuthToggle" />
      <UiCheckbox
        v-model="isAge"
        class="registration-form__checkbox"
        :value-true="1"
        :value-false="null"
        @input="$v.isAge.$reset()"
      >
        <template #label>
          {{ $t('forms.labels.registration.age') }}
        </template>
        <template #error>
          <div v-if="$v.isAge.$error">
            {{ $t('forms.errors.registration.confirmConditions') }}
          </div>
        </template>
      </UiCheckbox>
      <UiCheckbox
        v-model="isRead"
        class="registration-form__checkbox"
        :value-true="1"
        :value-false="null"
        @input="$v.isRead.$reset()"
      >
        <template #label>
          {{ $t('forms.labels.registration.read') }}
          <UiLink
            target="_blank"
            :href="`${$config.baseURL}/docs/PUBLIC_AGREEMENT.pdf`"
            color="green"
            :text="$t('forms.labels.registration.publicOffer')"
            class="registration-form__rules-link"
          />
          <UiLink
            target="_blank"
            :href="$paths.gameRules"
            color="green"
            :text="`${$t('forms.labels.registration.rules')},`"
            class="registration-form__rules-link"
          />
          <UiLink
            target="_blank"
            :href="$paths.policy"
            color="green"
            :text="$t('forms.labels.registration.policy')"
            class="registration-form__rules-link"
          />
        </template>
        <template #error>
          <div v-if="$v.isRead.$error">
            {{ $t('forms.errors.registration.confirmConditions') }}
          </div>
        </template>
      </UiCheckbox>
      <UiCheckbox
        v-model="isConfirmData"
        class="registration-form__checkbox"
        :value-true="1"
        :value-false="null"
        @input="$v.isConfirmData.$reset()"
      >
        <template #label>
          {{ $t('forms.labels.registration.isAccept') }}
        </template>
        <template #error>
          <div v-if="$v.isConfirmData.$error">
            {{ $t('forms.errors.registration.confirmConditions') }}
          </div>
        </template>
      </UiCheckbox>
    </form>

    <ModalRecaptcha v-model="isShowRecaptchaModal" @recaptcha-success="onRecaptchaSuccess" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, email, minLength } from 'vuelidate/lib/validators'
import { GTM_REG } from '@/constants/gtm'
import { AuthBy } from '~/util/composables/useLocalStorage'
import {
  phoneValidationErrorMessage,
  emailValidationErrorMessage,
  passwordValidationErrorMessage
} from '~/util/inputValidatonErrorMessage'
import { cyrillic, invalidSymbols } from '~/util/validators/vuelidateValidators'
import { storageService } from '@/services'

/** components */
import UiCheckbox from '@/components/ui/UiCheckbox'
import BaseInput from '@/components/Base/Input'
import ModalRecaptcha from '~/components/Modal/ModalRecaptcha'
import BaseInputPhone from '~/components/Base/Input/Phone'
import BaseInputPassword from '~/components/Base/Input/Password'
import BaseInputEmail from '~/components/Base/Input/Email'
import RegAuthToggle from '@/components/BigModal/RegAuthForgot/RegAuthToggle'

const initErrors = () => ({
  phone: [],
  email: [],
  password: [],
})

export default {
  name: 'RegistrationForm',
  components: {
    ModalRecaptcha,
    BaseInput,
    UiCheckbox,
    BaseInputPhone,
    BaseInputPassword,
    BaseInputEmail,
    RegAuthToggle,
  },
  mixins: [validationMixin],
  validations() {
    const commonFields = {
      password: { required, cyrillic, invalidSymbols, minLength: minLength(6) },
      isAge: { required },
      isRead: { required },
      isConfirmData: { required },
    }
    if (this.isActiveTabEmail) {
      return {
        email: {
          required,
          email,
          notOnlyNumbersInDomain: value => {
            const atIndex = value.indexOf('@')
            if (atIndex === -1) return true

            const domain = value.slice(atIndex + 1).toLowerCase()

            const domainParts = domain.split('.')

            if (domainParts.some(part => /^\d+$/.test(part))) {
              return false
            }

            return true
          },
          notStopDomain: value => {
            const atIndex = value.indexOf('@')
            if (atIndex === -1) return true

            const domain = value.slice(atIndex + 1).toLowerCase()
            return !this.$options.static.stopDomains.includes(domain)
          },
        },
        ...commonFields,
      }
    }
    else {
      return {
        phone: {
          tel: {
            required,
            custom() {
              return this.isInternalValidPhone
            },
          },
        },
        ...commonFields,
      }
    }
  },
  data() {
    return {
      /** form */
      phone: { tel: '', code: '+38' },
      email: '',
      password: '',
      isAge: 1,
      isRead: 1,
      isConfirmData: 1,
      /** request logic */
      isSubmitDisabled: false,
      /** error handle */
      errorMessages: initErrors(),
      // TODO: temporarily disabled
      // isOpenModalLogin: false,
      storageService,
      GTM_REG,
      recaptchaToken: '',
      isShowRecaptchaModal: false,
      isInternalValidPhone: false,
    }
  },
  static: {
    stopDomains: [
      'hi2.in',
      'temp-mail.org',
      'brinkc.com',
      '10minutemail.net',
    ],
  },
  computed: {
    ...mapGetters('registration', [
      'get_registerErrors',
      'get_isCaptchaRequired',
    ]),
    ...mapGetters('user', ['userId', 'userExistsData']),
    isRecaptchaModalShouldBeShow() {
      return this.get_isCaptchaRequired && !this.recaptchaToken
    },
    isActiveTabPhone() {
      return this.activeTab === 'phone'
    },
    isActiveTabEmail() {
      return this.activeTab === 'email'
    },
    activeTab() {
      return this.$route.query.bigModalTab
    },
    formHasDisplayedErrors() {
      return Boolean(
        this.errorMessages.phone.length
        || this.errorMessages.email.length
        || this.errorMessages.password.length
        || this.$v.isAge.$error
        || this.$v.isRead.$error
        || this.$v.isConfirmData.$error
      )
    },
    showRegAuthToggle() {
      return (
        this.$route.query?.bigModal === 'registration'
      )
    },
    isAccept() {
      return this.isAge && this.isRead && this.isConfirmData
    },
  },
  watch: {
    activeTab: 'resetErrors',
  },
  created() {
    if (process.client) {
      if (Object.keys(this.userExistsData).length) {
        this.email = this.userExistsData?.email
        this.phone = { code: this.userExistsData?.code, tel: this.userExistsData?.tel }
      }
    }
  },
  mounted() {
    this.isShowRecaptchaModal = this.isRecaptchaModalShouldBeShow
    const tabValue = this.$route.query.tabValue
    if (!!tabValue) {
      this.isActiveTabEmail ? this.email = tabValue : this.phone.tel = tabValue
    }
  },
  beforeDestroy() {
    this.$recaptcha.destroy()
  },
  methods: {
    ...mapActions('registration', ['act_register']),
    phoneValidationErrorMessage,
    emailValidationErrorMessage,
    passwordValidationErrorMessage,
    onRecaptchaSuccess(recaptchaToken) {
      this.recaptchaToken = recaptchaToken
      this.isShowRecaptchaModal = false
    },
    resetErrors() {
      this.errorMessages = initErrors()
      this.$v.$reset()
    },
    updateErrors() {
      this.errorMessages.phone = []
      this.errorMessages.email = []
      this.errorMessages.password = []
      if (this.$v.$invalid) {
        if (this.$v?.phone?.$error) {
          this.errorMessages.phone = this.phoneValidationErrorMessage(this.$v.phone.tel)
        }
        if (this.$v?.email?.$error) {
          this.errorMessages.email = this.emailValidationErrorMessage(this.$v.email)
        }
        if (this.$v?.password?.$error) {
          this.errorMessages.password = this.passwordValidationErrorMessage(this.$v.password)
        }
      }
    },
    async onSubmit() {
      if (this.isRecaptchaModalShouldBeShow) {
        this.isShowRecaptchaModal = true
        return
      }
      if (this.isSubmitDisabled || this.formHasDisplayedErrors) return
      this.isSubmitDisabled = true
      await this.$store.dispatch('gtm/push', {
        vCategory: 'modal_signup',
        vAction: 'signup_click_send',
      })
      this.$v.$touch()
      this.updateErrors()
      if (this.$v.$invalid) {
        this.isSubmitDisabled = false
        return
      }
      await this.registration()
    },
    async registration() {
      const promoCode = this.storageService.getItem('promoCode')

      const payload = {
        password: this.password,
        type: this.activeTab,
        is_accept: this.isAccept,
        language: this.$i18n.locale === 'ua' ? 'uk' : this.$i18n.locale,
        promokey: promoCode ? promoCode : '',
      }
      if (this.get_isCaptchaRequired) {
        payload.recaptchaToken = this.recaptchaToken
      }
      if (this.isActiveTabEmail) {
        payload.email = this.email
      }
      else {
        payload.phone = this.phone.tel
        payload.phone_code = this.phone.code
      }
      try {
        const res = await this.act_register(payload)
        this.recaptchaToken = ''
        this.$gtm.push({ event: 'FE_Registration', user_id: this.userId })
        if (res.status) {
          this.$gtm.push({
            event: GTM_REG.EVENT.ga_registration_successful,
            method: this.isActiveTabEmail ? 'email' : 'phone',
          })
          this.toRegistrationBonuses()
          this.storageService.removeItem('promoCode')
          AuthBy.value = this.isActiveTabEmail ? 'email' : 'phone'
        }
        await this.$recaptcha.reset()
      }
      catch (error) {
        this.recaptchaToken = ''
        if (this.get_registerErrors.message?.email === 'user_exists') {
          this.errorMessages.email.push(this.$t('popup.errors.emailBusy'))
          this.$store.commit('user/userExistsData', { email: this.email })
          // TODO: temporarily disabled
          // this.isOpenModalLogin = true
        }
        if (this.get_registerErrors.message?.phone === 'user_exists') {
          this.errorMessages.phone.push(this.$t('popup.errors.telBusy'))
          this.$store.commit('user/userExistsData', { tel: this.phone.tel, code: this.phone.code })
          // TODO: temporarily disabled
          // this.isOpenModalLogin = true
        }
        if (this.get_registerErrors.password === 'bad_password') {
          this.errorMessages.password.push(this.$t('forms.errors.maxPassword'))
        }
      }
      finally {
        this.isSubmitDisabled = false
        this.isShowRecaptchaModal = this.isRecaptchaModalShouldBeShow
      }
    },
    toRegistrationBonuses() {
      this.$newModal.changeQueryParam(
        'bigModal',
        'registrationBonuses',
        this.$route.path.includes(this.$paths.registration) ? '/' : undefined,
        true
      )
    },
  },
}
</script>

<style lang="scss" scoped src="./style.scss" />
